<script lang="ts" setup>
import numeral from 'numeral'
import GoodsImage from '~/components/common/modules/units/goods-image.vue'
import GoodsImageUrl from '~/components/common/modules/units/goods-image-url.vue'
import { MOBILE_IMAGE_SIZE } from '~/constants/common/good-image-size'
import {
  type GoodsItem,
  type GoodsItemConfig,
  type GoodsItemEmits,
  VIEW_TYPE,
  PRICE_TYPE
} from '~/components/common/modules/types/goods-item-type'
import { type GoodsImageConfig, GOODS_IMAGE_TYPE } from '~/components/common/modules/types/goods-image-type'
import { type GoodsImageUrlConfig } from '~/components/common/modules/types/goods-image-url-type'
import { useStoreGaECommerce, GA_EVENT_TYPE } from '~/composable/store/ga/use-store-ga-e-commerce'
import {
  GA_GOODS_ITEM_UNIT_WRAP,
  GA_GOODS_ITEM_IMG_WRAP
} from '~/composable/store/ga/common/store-ga-goods-click-constant'

defineOptions({
  name: 'DetailGoodsItem'
})

/**
 * 상품 속성
 * @typedef {Object} props
 * @property {Object} item - [필수] 상품데이터
 * @property {Object} config - 상품 설정
 */
const props = defineProps({
  item: Object as PropType<GoodsItem>,
  config: Object as PropType<GoodsItemConfig>,
  indexNumber: Number as PropType<number>
})

const propsItem = computed(() => props.item)
const propsConfig = computed(() => props.config)
const indexNumber = computed(() => props.indexNumber ?? 0)

// --------------------------------------------------- [아이템]
const image = computed(() => propsItem.value?.image)
const wishYn = computed(() => propsItem.value?.wishYn)
const onAirText = computed(() => propsItem.value?.onAirText)
const productNm = computed(() => propsItem.value?.productNm)
const priceArea = computed(() => propsItem.value?.priceArea)

// --------------------------------------------------- [설정]
const viewType = computed(() => propsConfig.value?.viewType ?? VIEW_TYPE.LIST)
const isBroadcastAvailableBuy = computed(() => propsConfig.value?.isBroadcastAvailableBuy)
const isAdultGoods = computed(() => propsConfig.value?.isAdultGoods ?? false)
const isUrlType = computed(() => propsConfig.value?.isUrlType)
const isShowWish = computed(() => propsConfig.value?.isShowWish ?? false)
const isShowOnlyDcPrice = computed(() => propsConfig.value?.isShowOnlyDcPrice ?? false)

// 찜
const isWish = computed(() => wishYn.value === 'Y')
// 와이드_유무
const isWide = computed(() => viewType.value === VIEW_TYPE.WIDTH)
const alt = computed(() => productNm.value)

// 할인유무
const isDc = computed(() => priceArea.value?.dcPrice < priceArea.value?.salePrice)
// 할인율_포맷팅
const dcRateFormat = computed(() => numeral(priceArea.value?.dcRate).divide(100).format('0%'))
// 최종판매가_포맷팅
const dcPriceFormat = computed(() => numeral(priceArea.value?.dcPrice).format('0,0'))
// 적용매가_포맷팅
const salePriceFormat = computed(() => numeral(priceArea.value?.salePrice).format('0,0'))
// 가격영역표시 > 가격_유무
const isPriceType = computed(() => priceArea.value?.type === PRICE_TYPE.PRICE)
// 가격영역표시 > 상담 상품일 경우 가격대신 텍스트 대체
const goodsText = computed(() => priceArea.value?.goodsText)

const propsWidth = computed(() => {
  const isWidth = Boolean(propsConfig.value?.width)
  if (isWidth === false && viewType.value === VIEW_TYPE.LIST) {
    return MOBILE_IMAGE_SIZE.GOOD_UNIT.LIST.WIDTH
  } else if (isWidth === false && viewType.value === VIEW_TYPE.GRID) {
    return MOBILE_IMAGE_SIZE.GOOD_UNIT.GRID.WIDTH
  } else if (isWidth === false && viewType.value === VIEW_TYPE.WIDTH) {
    return MOBILE_IMAGE_SIZE.GOOD_UNIT.WIDE.WIDTH
  } else if (isWidth) {
    return propsConfig.value?.width
  } else {
    return undefined
  }
})
const propsHeight = computed(() => {
  const isHeight = Boolean(propsConfig.value?.height)
  if (isHeight === false && viewType.value === VIEW_TYPE.LIST) {
    return MOBILE_IMAGE_SIZE.GOOD_UNIT.LIST.HEIGHT
  } else if (isHeight === false && viewType.value === VIEW_TYPE.GRID) {
    return MOBILE_IMAGE_SIZE.GOOD_UNIT.GRID.HEIGHT
  } else if (isHeight === false && viewType.value === VIEW_TYPE.WIDTH) {
    return MOBILE_IMAGE_SIZE.GOOD_UNIT.WIDE.HEIGHT
  } else if (isHeight) {
    return propsConfig.value?.height
  } else {
    return undefined
  }
})
const goodsImageType = computed(() => {
  if (viewType.value === VIEW_TYPE.WIDTH) {
    return GOODS_IMAGE_TYPE.WIDE
  } else {
    return GOODS_IMAGE_TYPE.REP
  }
})

const goodsImageConfig = ref<GoodsImageConfig>({
  width: propsWidth.value,
  height: propsHeight.value,
  type: goodsImageType.value,
  isAdult: isAdultGoods.value,
  alt: alt.value
})

const goodsImageUrlConfig = ref<GoodsImageUrlConfig>({
  isWide: isWide.value,
  isAdult: isAdultGoods.value,
  alt: alt.value
})

// 이벤트 셋팅
const emit = defineEmits<GoodsItemEmits>()

function _onChangeWish() {
  emit('onChangeWish', isWish.value)
}

// --------------------------------------------------- [GA]
const storeGaEvent = useStoreGaECommerce()
function onClickGoodsItem(event: Event, targetItem: any) {
  storeGaEvent.storeGaEventAction(GA_EVENT_TYPE.GOODS_CLICK, {
    goodsItem: targetItem,
    indexNumber: indexNumber.value,
    eventInfo: event
  })
}
</script>

<template>
  <div class="unit-wrap" :class="[viewType, GA_GOODS_ITEM_UNIT_WRAP]" :data-content-index="indexNumber">
    <figure class="img-wrap" :class="[GA_GOODS_ITEM_IMG_WRAP]">
      <router-link :to="`/goods/${image?.goodsCd}`" @click="onClickGoodsItem($event, propsItem)"
        >>
        <GoodsImageUrl
          v-if="isUrlType"
          :image-url="image?.imageUrl"
          :webp-url="image?.webpUrl"
          :config="goodsImageUrlConfig"
        />
        <GoodsImage v-else :goods-cd="image?.goodsCd" :config="goodsImageConfig" />

        <!-- 방송중_텍스트 -->
        <span v-if="isBroadcastAvailableBuy" class="on-air-text">{{ onAirText }}</span>
      </router-link>
      <!-- 찜 -->
      <button v-if="isShowWish" type="button" class="btn-wish" :class="{ on: isWish }" @click="_onChangeWish">
        찜
      </button>
    </figure>

    <router-link :to="`/goods/${image?.goodsCd}`" class="goods-info" @click="onClickGoodsItem($event, propsItem)">
      <!-- 상품명 -->
      <strong v-if="productNm" class="goods-name">{{ productNm }}</strong>

      <div v-if="isPriceType" class="price-wrap">
        <span v-if="isShowOnlyDcPrice" class="current-price"> {{ dcPriceFormat }}<em>원</em> </span>
        <template v-else>
          <!-- 할인율 -->
          <span v-if="isDc && priceArea?.dcRate >= 1" class="dc-rate">{{ dcRateFormat }}</span>
          <!-- 최종판매가 -->
          <span :class="[isDc ? 'dc-price' : 'current-price']"> {{ dcPriceFormat }}<em>원</em> </span>
          <!-- 적용매가 -->
          <del v-if="isDc" class="origin-price">{{ salePriceFormat }}원</del>
        </template>
      </div>
      <div v-else class="price-wrap">
        <!-- 상담 상품일 경우 가격대신 텍스트 대체 -->
        <span class="dc-price"> {{ goodsText }}</span>
      </div>
    </router-link>
  </div>
</template>

<style lang="scss" scoped>
@use '~/components/scss/common/modules/units/goods-item.scss';
</style>
